import { useState } from "react";
import { Loader2, LogIn, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api, errText } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState("");

  const sendReset = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); setSent(data.message); } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try { await login(email, password); } catch (x) { setErr(errText(x)); }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-plum-900 p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div className="grid-bg absolute inset-0 opacity-20" />
        <div className="relative text-xs uppercase tracking-[0.25em] text-plum-300">Counselor Desk · AY 2027-2028</div>
        <div className="relative">
          <h1 className="font-display text-5xl font-bold leading-tight">Admissions,<br />receipts &amp; follow-ups<br />in one place.</h1>
          <p className="mt-4 max-w-md text-slate-300">Fee calculations match the official counsellor receipt workbook exactly.</p>
        </div>
        <div className="relative text-xs text-slate-400">Staff access only</div>
      </div>
      <div className="flex items-center justify-center p-6">
        {forgot ? (
          <form onSubmit={sendReset} className="fade-up w-full max-w-sm space-y-5" data-testid="forgot-form">
            <img src="/logo.png" alt="Indo Scots Global School" className="h-14 w-auto" />
            <div>
              <h2 className="font-display text-2xl font-bold text-plum-950">Forgot password</h2>
              <p className="mt-1 text-sm text-slate-500">Enter your staff email and we'll send you a link to set a new password.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="femail" className="text-xs text-slate-600">Email</Label>
              <Input id="femail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white" data-testid="forgot-email-input" />
            </div>
            {sent && <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" data-testid="forgot-sent-msg">{sent} Check your inbox (and spam folder). The link is valid for 24 hours.</p>}
            {err && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="forgot-error">{err}</p>}
            <Button type="submit" disabled={busy} className="w-full bg-plum-700 hover:bg-plum-800" data-testid="forgot-submit-btn">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}{sent ? "Send again" : "Send reset link"}
            </Button>
            <button type="button" onClick={() => { setForgot(false); setErr(""); setSent(""); }} className="flex items-center gap-1 text-sm font-medium text-plum-700 hover:underline" data-testid="forgot-back-btn">
              <ArrowLeft className="h-4 w-4" />Back to sign in
            </button>
          </form>
        ) : (
        <form onSubmit={submit} className="fade-up w-full max-w-sm space-y-5" data-testid="login-form">
          <img src="/logo.png" alt="Indo Scots Global School" className="h-14 w-auto" />
          <div>
            <h2 className="font-display text-2xl font-bold text-plum-950">Counsellor Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">Use the account your admin created for you.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs text-slate-600">Email</Label>
            <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-white" data-testid="login-email-input" />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs text-slate-600">Password</Label>
              <button type="button" onClick={() => { setForgot(true); setErr(""); }} className="text-xs font-medium text-plum-700 hover:underline" data-testid="forgot-password-link">Forgot password?</button>
            </div>
            <PasswordInput id="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-white" data-testid="login-password-input" />
          </div>
          {err && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">{err}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-plum-700 hover:bg-plum-800" data-testid="login-submit-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}Sign in
          </Button>
        </form>
        )}
      </div>
    </div>
  );
}
